import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';

const Courses = () => {
  const [currentView, setCurrentView] = useState('faculties'); // 'faculties', 'semesters', 'courses'
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);

  // Scalable faculty data structure - easy to add more faculties
  const facultiesData = {
    BCT: {
      id: 'BCT',
      name: 'Computer Engineering',
      shortName: 'BCT',
      icon: '💻',
      description: 'Software, Hardware & Networks',
      color: 'bg-blue-50',
      iconColor: 'text-blue-600',
      semesters: 8,
    },
    BEX: {
      id: 'BEX',
      name: 'Electronics Engineering',
      shortName: 'BEX',
      icon: '🔌',
      description: 'Circuits & Communication',
      color: 'bg-purple-50',
      iconColor: 'text-purple-600',
      semesters: 8,
    },
    BEL: {
      id: 'BEL',
      name: 'Electrical Engineering',
      shortName: 'BEL',
      icon: '⚡',
      description: 'Power Systems & Controls',
      color: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      semesters: 8,
    },
  };

  // Sample semester subjects - easily expandable
  const getSemesterSubjects = (facultyId, semester) => {
    // This can be replaced with API call or database lookup
    const subjectsMap = {
      1: ['Engineering Mathematics I', 'Physics', 'Chemistry', 'Programming in C', 'Engineering Drawing'],
      2: ['Engineering Mathematics II', 'Electrical Engineering', 'Thermodynamics', 'Workshop Technology', 'Communication Techniques'],
      3: ['Engineering Mathematics III', 'Data Structures', 'Digital Logic', 'Theory of Computation', 'Probability & Statistics'],
      4: ['Numerical Methods', 'Computer Architecture', 'Operating Systems', 'Database Management', 'Software Engineering'],
      5: ['Design & Analysis of Algorithms', 'Computer Networks', 'Computer Graphics', 'Web Technology', 'Embedded Systems'],
      6: ['Artificial Intelligence', 'Distributed Systems', 'Compiler Design', 'Information Security', 'Object Oriented Design'],
      7: ['Cloud Computing', 'Mobile Application Development', 'Big Data', 'Internet of Things', 'Elective I'],
      8: ['Machine Learning', 'Blockchain Technology', 'Project Management', 'Professional Ethics', 'Elective II'],
    };
    return subjectsMap[semester] || [];
  };

  // Confetti effect for bundle card
  useEffect(() => {
    if (currentView === 'courses') {
      const container = document.querySelector('.confetti-container');
      if (!container) return;

      const colors = ['#fbbf24', '#f59e0b', '#d97706', '#ef4444', '#ec4899', '#8b5cf6'];
      const confettiCount = 30;

      // Clear existing confetti
      container.innerHTML = '';

      for (let i = 0; i < confettiCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDuration = `${3 + Math.random() * 4}s`;
        confetti.style.animationDelay = `${Math.random() * 2}s`;
        confetti.style.width = `${5 + Math.random() * 8}px`;
        confetti.style.height = `${5 + Math.random() * 8}px`;
        container.appendChild(confetti);
      }
    }
  }, [currentView]);

  const handleFacultyClick = (facultyId) => {
    setSelectedFaculty(facultyId);
    setCurrentView('semesters');
  };

  const handleSemesterClick = (semester) => {
    setSelectedSemester(semester);
    setCurrentView('courses');
  };

  const handleBack = () => {
    if (currentView === 'courses') {
      setCurrentView('semesters');
      setSelectedSemester(null);
    } else if (currentView === 'semesters') {
      setCurrentView('faculties');
      setSelectedFaculty(null);
    }
  };

  const renderBreadcrumb = () => {
    return (
      <div className="mb-6">
        {currentView !== 'faculties' && (
          <button
            onClick={handleBack}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to {currentView === 'courses' ? 'Semesters' : 'Faculties'}
          </button>
        )}
      </div>
    );
  };

  // Faculty Selection View
  const renderFaculties = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Faculty</h2>
        <p className="text-xl text-gray-600">Select your engineering program to get started</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Object.values(facultiesData).map((faculty) => (
          <div
            key={faculty.id}
            onClick={() => handleFacultyClick(faculty.id)}
            className={`faculty-card rounded-2xl p-8 shadow-lg ${faculty.color}`}
          >
            <div className="text-center">
              <div className={`text-6xl mb-4 ${faculty.iconColor}`}>{faculty.icon}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{faculty.shortName}</h3>
              <p className="text-lg font-semibold text-gray-700 mb-2">{faculty.name}</p>
              <p className="text-gray-600 mb-4">{faculty.description}</p>
              <div className="inline-flex items-center text-blue-600 font-semibold">
                Explore Courses
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Semester Selection View - Minimalist Grid Design
  const renderSemesters = () => {
    const faculty = facultiesData[selectedFaculty];
    if (!faculty) return null;

    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {renderBreadcrumb()}

        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {faculty.name}
          </h2>
          <p className="text-gray-600">Select your semester</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {Array.from({ length: faculty.semesters }, (_, i) => i + 1).map((semester) => (
            <div
              key={semester}
              onClick={() => handleSemesterClick(semester)}
              className="group relative bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-blue-500 hover:-translate-y-2"
            >
              <div className="text-center">
                <div className="text-5xl font-black text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {semester}
                </div>
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Semester
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/5 group-hover:to-purple-500/5 rounded-2xl transition-all duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Add state for syllabus modal
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Helper function to generate consistent dummy data for subjects
  const getSubjectStats = (subjectName) => {
    // Generate consistent random numbers based on subject name length
    const seed = subjectName.length;
    const chapters = 8 + (seed % 7); // 8-14 chapters
    const totalQuestions = 180 + (seed * 15 % 120); // 180-300 questions
    const variations = 450 + (seed * 25 % 350); // 450-800 variations

    return { chapters, totalQuestions, variations };
  };

  // Add duration state
  const [durationType, setDurationType] = useState('yearly'); // 'yearly' or 'lifetime'

  // Courses View with Comprehensive Pricing Options
  const renderCourses = () => {
    const faculty = facultiesData[selectedFaculty];
    const subjects = getSemesterSubjects(selectedFaculty, selectedSemester);

    // Pricing calculations
    const pricing = {
      yearly: {
        individual: 399,
        bundle: 250,
      },
      lifetime: {
        individual: 899,
        bundle: 750,
      }
    };

    const current = pricing[durationType];
    const individualTotal = current.individual * subjects.length;
    const bundleTotal = current.bundle * subjects.length;
    const savingsAmount = individualTotal - bundleTotal;
    const savingsPercent = Math.round((savingsAmount / individualTotal) * 100);

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {renderBreadcrumb()}

        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {faculty?.name} - Semester {selectedSemester}
          </h2>
          <p className="text-xl text-gray-600">Choose your access duration and plan</p>
        </div>

        {/* DURATION TOGGLE */}
        <div className="flex justify-center mb-8">
          <div className="duration-toggle-container">
            <button
              onClick={() => setDurationType('yearly')}
              className={`duration-toggle-btn ${durationType === 'yearly' ? 'active' : ''}`}
            >
              <div className="flex items-center justify-center gap-2">
                <div className="text-base font-bold">Full Semester</div>
                <span className="recommended-badge">RECOMMENDED</span>

              </div>
              <div className="text-xs opacity-90">Best for Students</div>
            </button>
            <button
              onClick={() => setDurationType('lifetime')}
              className={`duration-toggle-btn ${durationType === 'lifetime' ? 'active' : ''}`}
            >
              <div className="flex items-center justify-center gap-2">
                <div className="text-base font-bold">Lifetime Access</div>
              </div>
              <div className="text-xs opacity-90">Yearly Updates for Teachers</div>
            </button>
          </div>
        </div>



        {/* PRICING CARDS COMPARISON */}
        <div className="grid md:grid-cols-2 gap-4 mb-12 max-w-5xl mx-auto">          {/* BUNDLE CARD - FEATURED */}
          <div className="pricing-card-featured">
            <div className="confetti-container"></div>

            {/* Best Value Badge - Diagonal Corner */}
            <span className="best-value-badge-new">🎉 BEST VALUE 🎉</span>

            <div className="relative z-10 p-8">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-black pt-2 text-gray-900 mb-2">
                  Complete Bundle
                </h3>
                <p className="text-gray-700 font-semibold">
                  All {subjects.length} subjects included
                </p>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-5xl font-black text-gray-900">₹{current.bundle}</span>
                  <span className="text-2xl text-gray-700 font-bold">/subject</span>
                </div>
                <div className="text-3xl font-bold text-green-700 mb-2">
                  Total: ₹{bundleTotal}
                </div>
                <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-sm">
                  Save ₹{savingsAmount} ({savingsPercent}% OFF)
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-gray-800 font-medium">
                  <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>All {subjects.length} subjects - Complete coverage</span>
                </div>
                <div className="flex items-center text-gray-800 font-medium">
                  <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{durationType === 'lifetime' ? 'Lifetime access - Forever!' : 'Full year access'}</span>
                </div>
                <div className="flex items-center text-gray-800 font-medium">
                  <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>All previous year questions</span>
                </div>
                <div className="flex items-center text-gray-800 font-medium">
                  <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Video lectures & study materials</span>
                </div>
                <div className="flex items-center text-gray-800 font-medium">
                  <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Practice questions & mock tests</span>
                </div>
                {durationType === 'lifetime' && (
                  <div className="flex items-center text-gray-800 font-medium">
                    <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-bold text-purple-600">Free future updates & additions</span>
                  </div>
                )}
              </div>

              <button className="btn-bundle-purchase">
                Get Complete Bundle - ₹{bundleTotal}
              </button>
              <p className="text-center text-xs text-gray-600 mt-2 font-semibold">
                {durationType === 'lifetime' ? '⚡ One-time payment, lifetime access' : '🔥 Full semester access'}
              </p>
            </div>
          </div>

          {/* INDIVIDUAL SUBJECTS CARD */}
          <div className="pricing-card-standard">
            <div className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-black pt-2 text-gray-900 mb-2">
                  Individual Subjects
                </h3>
                <p className="text-gray-600 font-semibold">
                  Pick and choose specific subjects
                </p>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <span className="text-5xl font-black text-gray-900">₹{current.individual}</span>
                  <span className="text-2xl text-gray-700 font-bold">/subject</span>
                </div>
                <div className="text-2xl font-bold text-gray-600 mb-2">
                  Total (all {subjects.length}): ₹{individualTotal}
                </div>
                <div className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full font-bold text-sm">
                  Costs ₹{savingsAmount} more than bundle
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-700">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Select specific subjects only</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{durationType === 'lifetime' ? 'Lifetime access per subject' : 'Full year access per subject'}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Study materials included</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Practice questions</span>
                </div>
                <div className="flex items-center text-gray-400 line-through">
                  <svg className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>Bundle savings (miss out on {savingsPercent}% off)</span>
                </div>
              </div>

              <button className="btn-individual-browse">
                Browse Individual Subjects
              </button>
            </div>
          </div>
        </div>

        {/* ENHANCED SUBJECTS LIST - Individual Pricing Only */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            📚 All Available Subjects
          </h3>
          <p className="text-center text-gray-600 mb-8">
            Individual pricing at ₹{current.individual} per subject. Click any card to view detailed syllabus.
          </p>




          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-9">
            {subjects.map((subject, index) => {
              const stats = getSubjectStats(subject);
              // Curated list of professional education/tech images that ALWAYS work
              const courseImages = [
                "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=800&q=80", // Library
                "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80", // Laptop/Notes
                "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=800&q=80", // Coding
                "https://images.unsplash.com/photo-1501504905252-473c47e087f8?auto=format&fit=crop&w=800&q=80", // Coffee/Study
                "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=800&q=80", // Macbook
                "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80", // Tech
                "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80", // Office
                "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=800&q=80"  // Pen/Paper
              ];
              const courseImage = courseImages[index % courseImages.length];
              const courseId = subject.replace(/\s+/g, '-').toLowerCase();

              return (
                <div
                  key={index}
                  onClick={() => window.location.href = `/courses/${courseId}`}
                  className="group bg-white rounded-xl border border-gray-400 hover:border-blue-400 hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col h-full overflow-hidden relative"
                >
                  {/* Image Section with Floating Badges */}
                  <div className="relative h-44 overflow-hidden bg-gray-200">
                    <img
                      src={courseImage}
                      alt={subject}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>

                    {/* Floating Duration Badge */}
                    <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-md text-xs font-medium text-white border border-white/20">
                      {durationType === 'lifetime' ? 'Lifetime Access' : '1 Year Access'}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-4 flex flex-col flex-grow">

                    {/* Title - Moved to Top & Bigger Font */}
                    <h3 className="font-bold text-gray-900 text-xl leading-tight mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">
                      {subject}
                    </h3>

                    {/* Faculty & Semester - Moved Below Title */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-100 uppercase tracking-wide">
                        {faculty?.shortName || 'BCT'}
                      </span>
                      <span className="text-xs font-semibold text-gray-500">
                        Semester {selectedSemester}
                      </span>
                    </div>

                    {/* Stats Grid - The Style You Liked (Cleaned Up) */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2.5 mb-5 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full ring-2 ring-blue-100"></span>
                        <span>{stats.totalQuestions} Questions</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full ring-2 ring-green-100"></span>
                        <span>{stats.chapters} Chapters</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full ring-2 ring-purple-100"></span>
                        <span>{Math.floor(stats.totalQuestions / 3)} Master Qs</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full ring-2 ring-orange-100"></span>
                        <span>{stats.variations} Variations</span>
                      </div>
                    </div>

                    {/* Footer: Price & Action */}
                                     {/* Footer: Price & Action */}
                    <div className="mt-auto flex items-center justify-between pt-2 border-t border-gray-100">
                      <div>
                        <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Price</p>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-2xl font-black text-black-600">₹{current.individual}</span>
                          <span className="text-3xs text-red-400 line-through font-medium">₹{Math.floor(current.individual + 200)}</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSubject(subject);
                        }}
                        className="bg-gray-900 hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-all shadow-lg shadow-gray-200 hover:shadow-blue-200 hover:-translate-y-0.5 flex items-center gap-2"
                      >
                        <span>Add to Cart</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>





        </div>

        {/* Syllabus Modal */}
        {selectedSubject && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedSubject(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-black text-gray-900 mb-2">{selectedSubject}</h2>
                  <p className="text-gray-600">Detailed Syllabus & Chapter Breakdown</p>
                </div>
                <button onClick={() => setSelectedSubject(null)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Sample Syllabus - Using Dummy Data */}
                {[...Array(getSubjectStats(selectedSubject).chapters)].map((_, idx) => (
                  <div key={idx} className="border-2 border-gray-400 rounded-lg p-4 hover:border-blue-400 transition-colors">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      Chapter {idx + 1}: Sample Topic {idx + 1}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Comprehensive coverage of key concepts and practical applications.
                    </p>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>📝 {Math.floor(Math.random() * 15) + 10} Questions</span>
                      <span>🔄 {Math.floor(Math.random() * 30) + 20} Variations</span>
                      <span>⏱️ {Math.floor(Math.random() * 3) + 2}h content</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex gap-3">
                <button className="flex-1 btn-primary py-3 rounded-lg font-bold">
                  Enroll Now - ₹{current.individual}
                </button>
                <button onClick={() => setSelectedSubject(null)} className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FINAL CTA SECTION */}
        <div className="final-cta-section">
          <div className="text-center">
            <h3 className="text-4xl font-black text-white mb-4">
              🎓 You will know "What to Study" for Exams.
            </h3>
            <p className="text-xl text-white/90 mb-6">
              {durationType === 'lifetime'
                ? `Get lifetime access to all ${subjects.length} subjects for just ₹${bundleTotal} - that's only ₹${current.bundle} per subject!`
                : `Get full year access to all ${subjects.length} subjects for just ₹${bundleTotal} - that's only ₹${current.bundle} per subject!`}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button className="cta-btn-primary">
                🎉 Get Complete Bundle - ₹{bundleTotal}
              </button>
              <button className="cta-btn-secondary">
                Browse Individual Subjects
              </button>
            </div>
            <p className="text-white/80 mt-4 text-sm">
              Even Tuition for 1 subject cost - Rs5000/Month.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white pt-5 text-gray-900 min-h-screen">
      <Navbar />
      {/* Main Content */}
      <section className="bg-gray-50 min-h-[60vh]">
        {currentView === 'faculties' && renderFaculties()}
        {currentView === 'semesters' && renderSemesters()}
        {currentView === 'courses' && renderCourses()}
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gradient">Exam Prasna</h3>
              <p className="text-gray-400 mb-4">
                Professional learning platform built for Excellence
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="/courses" className="hover:text-white transition-colors">Courses</a></li>
                <li><a href="/dashboard" className="hover:text-white transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 pb-4 text-center text-gray-400">
            <p>&copy; 2025 Exam Prasna. All rights reserved. Built with passion for learning.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Courses;